/**
 * Moving a matter to another stage.
 *
 * Three rules from the domain, all visible in this file:
 *
 * 1. A STAGE CHANGE IS A SUB-RESOURCE, NOT AN ATTRIBUTE WRITE.
 *    `POST /cases/{id}/transition/`, never `PATCH /cases/{id}/` — the PATCH cannot
 *    change `current_stage` at all. The sub-resource checks the whitelist, evaluates
 *    the guards, records history and emits the event in one transaction.
 *
 * 2. BLOCKED MOVES ARE SHOWN, NOT HIDDEN. `available_transitions` returns every
 *    declared move with `permitted` and a `reason`. Rendering only the permitted ones
 *    leaves a stuck matter with no explanation, and "why can't I close this?" is the
 *    question this list exists to answer. Blocked options are disabled with their
 *    reason beside them.
 *
 * 3. A 409 CARRIES A REASON MEANT FOR THE USER. The guards are re-evaluated on
 *    execution — the list can be seconds stale — so a refusal here is expected, not
 *    exceptional, and its `detail` is shown verbatim.
 *
 * 4. "NEEDS A REASON" IS NOT "BLOCKED". A move that only wants an explanation is
 *    selectable, and the Reason box becomes required when it is chosen. Rendering the
 *    two states identically produced a matter that could not be cancelled: the server
 *    reported Cancelled as blocked because no reason had been typed, this dialog
 *    disabled the option, and the box that would have unblocked it sat directly
 *    underneath describing itself as optional.
 */

import { ConflictError, useApiMutation, useApi, qk, type AvailableTransition, type CaseDetail } from '@legalcrm/api';
import { Badge, Modal, Button, Textarea, useToast } from '@legalcrm/ui';
import { useState } from 'react';

import { STAFF } from '@legalcrm/api';

import { StagePathList, useStageOverview } from './StagePath';

export interface TransitionDialogProps {
  open: boolean;
  onClose: () => void;
  caseRecord: CaseDetail;
  transitions: AvailableTransition[];
}

export function TransitionDialog({
  open,
  onClose,
  caseRecord,
  transitions,
}: TransitionDialogProps) {
  const api = useApi();
  const { toast } = useToast();
  const [selected, setSelected] = useState<string | null>(null);
  // A pick on the path: skipping ahead or correcting back, instead of the next move.
  const [jump, setJump] = useState<{ key: string; action: 'skip' | 'correct' } | null>(null);
  const [reason, setReason] = useState('');
  const overview = useStageOverview(caseRecord.id, open);
  const pausedAt = overview.data?.paused_at ?? null;

  const chosen = transitions.find((t) => t.to_stage_key === selected) ?? null;
  const jumpName = overview.data?.path.find((s) => s.key === jump?.key)?.name ?? '';
  // Both deliberate jumps always ask why; so do the moves the firm marked.
  const reasonRequired = jump !== null || (chosen?.requires_reason ?? false);
  const reasonMissing = reasonRequired && !reason.trim();
  const target = jump?.key ?? selected;

  const move = useApiMutation<CaseDetail, { to_stage: string; reason: string }>(
    {
      mutationFn: (body) =>
        jump
          ? api.post<CaseDetail>(`${STAFF.caseMoveStage(caseRecord.id)}/`, body, {
              ifMatch: caseRecord.version,
            })
          : api.post<CaseDetail>(STAFF.caseTransition(caseRecord.id), body, {
              // Optimistic concurrency: a stale write comes back 409 rather than
              // quietly overwriting somebody else's transition.
              ifMatch: caseRecord.version,
            }),
      invalidates: [qk.all('cases'), qk.all('case-timeline')],
    },
    {
      onSuccess: (updated) => {
        toast({
          title: 'Stage changed',
          description: `${caseRecord.case_number} is now at ${updated.current_stage.name}.`,
          tone: 'success',
        });
        onClose();
        setSelected(null);
        setJump(null);
        setReason('');
      },
      onError: (error) => {
        // The guards ran again on execution and refused. The reason is written for
        // the user; show it as given rather than paraphrasing it.
        toast({
          title: 'The move was refused',
          description:
            error instanceof ConflictError
              ? error.detail
              : 'The stage could not be changed. Try again.',
          tone: 'warning',
        });
      },
    },
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Change stage"
      description={`${caseRecord.case_number} is currently at ${caseRecord.current_stage.name}.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={move.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            // Held back rather than allowed to fail: the server refuses an empty
            // reason with a 409, and a round trip to be told what the form already
            // knows is a worse experience than a disabled button that says why.
            disabled={!target || reasonMissing}
            loading={move.isPending}
            onClick={() => target && move.mutate({ to_stage: target, reason })}
          >
            {jump?.action === 'skip'
              ? `Skip to ${jumpName}`
              : jump?.action === 'correct'
                ? `Move back to ${jumpName}`
                : 'Move'}
          </Button>
        </>
      }
    >
      {overview.data && overview.data.path.length > 0 && (
        <section className="mb-4 rounded border border-line bg-surface-sunken px-3 py-3">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-subtle">
            The whole path
          </h3>
          <StagePathList
            overview={overview.data}
            selected={jump?.key ?? null}
            onPick={(key, action) => {
              setSelected(null);
              setJump(jump?.key === key ? null : { key, action });
            }}
          />
          {(overview.data.can_skip || overview.data.can_correct) && (
            <p className="mt-2 text-xs text-ink-subtle">
              {overview.data.can_skip &&
                'Skipping marks the steps in between as done for the client. '}
              {overview.data.can_correct &&
                'Moving back corrects the stage quietly; the client is not notified.'}
            </p>
          )}
        </section>
      )}

      {transitions.length === 0 ? (
        <p className="text-sm text-ink-muted">
          There are no moves available from this stage.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-subtle">
              {pausedAt ? 'Resume or change' : 'Next move'}
            </legend>
            {transitions.map((transition) => (
              <label
                key={transition.to_stage_key}
                className={[
                  'flex cursor-pointer items-start gap-3 rounded border px-3 py-2.5 transition-colors',
                  transition.permitted
                    ? 'border-line-strong hover:bg-surface-sunken'
                    : 'cursor-not-allowed border-line bg-surface-sunken opacity-80',
                  selected === transition.to_stage_key && 'border-primary bg-primary-subtle',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <input
                  type="radio"
                  name="to_stage"
                  value={transition.to_stage_key}
                  disabled={!transition.permitted}
                  checked={selected === transition.to_stage_key}
                  onChange={() => {
                    setJump(null);
                    setSelected(transition.to_stage_key);
                  }}
                  className="mt-0.5 accent-[hsl(var(--primary))]"
                />
                <span className="min-w-0">
                  <span className="flex items-center gap-2">
                    <span className="text-base font-medium text-ink">
                      {pausedAt && transition.to_stage_key === pausedAt.key
                        ? `Resume — back to ${transition.to_stage_name}`
                        : transition.to_stage_name}
                    </span>
                    {!transition.permitted ? (
                      <Badge tone="warning" size="sm">
                        Blocked
                      </Badge>
                    ) : (
                      transition.requires_reason && (
                        // Not a warning tone. Nothing is wrong — the firm has simply
                        // decided this move is worth explaining, and colouring it like
                        // a refusal teaches people to read past the real ones.
                        <Badge tone="info" size="sm">
                          Reason required
                        </Badge>
                      )
                    )}
                  </span>
                  {/* The server's own words. This is the whole reason blocked
                      transitions are returned rather than omitted. */}
                  {transition.reason && (
                    <span className="mt-0.5 block text-sm text-ink-muted">
                      {transition.reason}
                    </span>
                  )}
                </span>
              </label>
            ))}
          </fieldset>

          <Textarea
            label="Reason"
            required={reasonRequired}
            hint={
              jump
                ? 'Required. Say why the matter is skipping or going back; it becomes part of the matter file.'
                : reasonRequired
                  ? `Required to move to ${chosen?.to_stage_name}. It becomes part of the matter file.`
                  : 'Recorded in the matter history. Optional, but useful to whoever reads this later.'
            }
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
      )}
    </Modal>
  );
}
